import { LoadingButton } from "@mui/lab";
import {
  Box,
  FormControl,
  FormHelperText,
  FormLabel,
  Grid,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { Helmet } from "react-helmet-async";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import Breadcrumb from "../../components/Breadcrumb";
import MainLayout from "../../components/MainLayout";
import ProtectedPermissionPage from "../../components/ProtectedPermissionPage";
import useNotification from "../../hooks/useNotification";
import forecastService from "../../services/forecast";
import regionService from "../../services/region";
import monthMapping from "../../utils/month";
import queryDefault from "../../utils/queryDefault";

export default function ForecastCreate() {
  const navigate = useNavigate();
  const notification = useNotification();

  const form = useForm({
    defaultValues: {
      region_id: "",
      month: 1,
      year: new Date().getFullYear(),
      roll_count_beginning: 0,
      kertas_count_beginning: 0,
      roll_count_end: 0,
      kertas_count_end: 0,
      used_kertas: 0,
    },
  });

  const listRegion = useQuery(["create-forecast-region-options"], () => regionService.getAllRegions({}), {
    ...queryDefault,
  });

  const createForecast = useMutation(forecastService.createForecast, {
    onSuccess: () => {
      notification.onOpen({
        message: "Forecast created successfully!",
        type: "success",
        position: "top",
      });
      navigate("/app/forecast");
    },
    onError: () => {
      notification.onOpen({
        message: "Failed to create forecast",
        type: "error",
        position: "top",
      });
    },
    ...queryDefault,
  });

  const onSubmit = (values: any) => {
    createForecast.mutate({
      region_id: Number(values.region_id),
      month: Number(values.month),
      year: Number(values.year),
      roll_count_beginning: Number(values.roll_count_beginning) || 0,
      kertas_count_beginning: Number(values.kertas_count_beginning) || 0,
      roll_count_end: Number(values.roll_count_end) || 0,
      kertas_count_end: Number(values.kertas_count_end) || 0,
      used_kertas: Number(values.used_kertas) || 0,
    });
  };

  return (
    <MainLayout>
      <Helmet>
        <title>Create Forecast</title>
      </Helmet>

      <Breadcrumb
        label="Create Forecast"
        breadcrumbs={[
          { label: "Dashboard", href: "/app" },
          { label: "Forecast", href: "/app/forecast" },
        ]}
      />

      <ProtectedPermissionPage acceptPermissions={[1, 2]}>
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            Forecast Detail
          </Typography>

          <Grid container>
            <Grid item lg={4} xs={12}>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel required>Region</FormLabel>
                <Controller
                  name="region_id"
                  control={form.control}
                  rules={{ required: "Region is required" }}
                  render={({ field, fieldState }) => (
                    <>
                      <Select
                        {...field}
                        size="small"
                        error={Boolean(fieldState.error?.message)}
                      >
                        {listRegion.data?.data?.map((item: any) => (
                          <MenuItem key={item.id} value={item.id}>
                            {item.name}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText error={Boolean(fieldState.error?.message)}>
                        {fieldState.error?.message}
                      </FormHelperText>
                    </>
                  )}
                />
              </FormControl>

              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel required>Month</FormLabel>
                <Controller
                  name="month"
                  control={form.control}
                  rules={{ required: "Month is required", min: 1, max: 12 }}
                  render={({ field, fieldState }) => (
                    <Select
                      {...field}
                      size="small">
                      {[...Array(12)].map((_, index) => (
                        <MenuItem key={index + 1} value={index + 1}>
                          {monthMapping[index + 1]}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </FormControl>

              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel required>Year</FormLabel>
                <Controller
                  name="year"
                  control={form.control}
                  rules={{ required: "Year is required" }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      type="number"
                      size="small"
                      error={Boolean(fieldState.error?.message)}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              </FormControl>

              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel required>Roll Count Beginning</FormLabel>
                <Controller
                  name="roll_count_beginning"
                  control={form.control}
                  render={({ field }) => <TextField {...field} type="number" size="small" />}
                />
              </FormControl>

              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel required>Kertas Count Beginning</FormLabel>
                <Controller
                  name="kertas_count_beginning"
                  control={form.control}
                  render={({ field }) => <TextField {...field} type="number" size="small" />}
                />
              </FormControl>

              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel required>Roll Count End</FormLabel>
                <Controller
                  name="roll_count_end"
                  control={form.control}
                  render={({ field }) => <TextField {...field} type="number" size="small" />}
                />
              </FormControl>

              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel required>Kertas Count End</FormLabel>
                <Controller
                  name="kertas_count_end"
                  control={form.control}
                  render={({ field }) => <TextField {...field} type="number" size="small" />}
                />
              </FormControl>

              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel required>Used Kertas</FormLabel>
                <Controller
                  name="used_kertas"
                  control={form.control}
                  render={({ field }) => <TextField {...field} type="number" size="small" />}
                />
              </FormControl>
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ mt: 3 }}>
          <LoadingButton
            variant="contained"
            loading={createForecast.isLoading}
            onClick={form.handleSubmit(onSubmit)}
          >
            Save
          </LoadingButton>
        </Box>
      </ProtectedPermissionPage>
    </MainLayout>
  );
}